import { KeyRound } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { ColocaTabs } from "@/shared/components/navigation/ColocaTabs";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";

import type { OrganizationFeatureDefinition } from "../constants/organizationsAdmin.constants";
import type { OrganizationOverviewState } from "../hooks/useOrganizationOverview";
import type {
  AdminOrganization,
  GlobalFeatures,
  OrganizationFeature,
} from "../types/organizationsAdmin.types";
import { organizationFeatureState } from "../utils/organizationFeatures";
import { DisablePayoutBatchesDialog } from "./DisablePayoutBatchesDialog";
import { OrganizationOverviewPanel } from "./OrganizationOverviewPanel";
import { OrganizationUsersPanel } from "./OrganizationUsersPanel";

export interface OrganizationFeaturesDrawerProps {
  /** Kept while the panel animates out, so it never renders empty. */
  organization: AdminOrganization | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The staff overview of this organization, read by the page each time the panel opens. */
  overview: OrganizationOverviewState;
  onReloadOverview: () => void;
  /** The features this reader may see. */
  catalog: readonly OrganizationFeatureDefinition[];
  globalFeatures: GlobalFeatures | null;
  /** A switch of this organization is being written: all of them wait. */
  isPending: boolean;
  /** Omitted for readers without `organizations:write`: every switch renders read-only. */
  onFeatureChange?: (
    organization: AdminOrganization,
    feature: OrganizationFeature,
    enabled: boolean,
  ) => Promise<void>;
  /** Only for the staff allowed to assign custom Bre-B keys. */
  onAssignCustomKey?: (organization: AdminOrganization) => void;
}

export function OrganizationFeaturesDrawer({
  organization,
  open,
  onOpenChange,
  overview,
  onReloadOverview,
  catalog,
  globalFeatures,
  isPending,
  onFeatureChange,
  onAssignCustomKey,
}: OrganizationFeaturesDrawerProps) {
  const { t } = useTranslations("organizations");
  const [confirmingOff, setConfirmingOff] = useState<AdminOrganization | null>(null);

  if (!organization) return null;

  const change = (definition: OrganizationFeatureDefinition, enabled: boolean) => {
    if (!onFeatureChange) return;
    // Its members lose the module at once, so switching it off is confirmed.
    if (!enabled && definition.feature === "payout_batches_enabled") {
      setConfirmingOff(organization);
      return;
    }
    void onFeatureChange(organization, definition.feature, enabled);
  };

  const features = (
    <section aria-labelledby="organization-features-title">
      <div className="flex items-baseline justify-between gap-3">
        <h3 id="organization-features-title" className="text-sm font-semibold text-foreground">
          {t("features_title")}
        </h3>
        <p className="text-xs text-muted-foreground">
          {onFeatureChange ? t("features_hint") : t("features_read_only")}
        </p>
      </div>
      <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
        {catalog.map((definition) => {
          const state = organizationFeatureState(organization, definition, globalFeatures);
          const Icon = definition.icon;
          return (
            <li key={definition.feature} className="flex items-start gap-3 p-4">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="size-4" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">{t(definition.labelKey)}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t(definition.descriptionKey)}</p>
                {state === "global" && (
                  <p className="mt-1.5 text-xs font-medium text-primary">{t("feature_global_note")}</p>
                )}
                {state === "blocked" && (
                  <p className="mt-1.5 text-xs font-medium text-destructive">
                    {organization[definition.feature]
                      ? t("feature_blocked_note_on")
                      : t("feature_blocked_note_off")}
                  </p>
                )}
                {definition.feature === "breb_receiving_key_enabled" && onAssignCustomKey && (
                  <ColocaButton
                    variant="secondary"
                    size="xs"
                    icon={KeyRound}
                    className="mt-2.5"
                    onClick={() => onAssignCustomKey(organization)}
                  >
                    {t("custom_key_action")}
                  </ColocaButton>
                )}
              </div>
              <ColocaSwitch
                size="sm"
                checked={state === "on" || state === "global"}
                disabled={state === "global" || state === "blocked" || !onFeatureChange || isPending}
                onCheckedChange={(checked) => change(definition, checked)}
                aria-label={t(definition.switchAriaKey, { name: organization.name })}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      pinLastChild={false}
      title={organization.name}
      description={organization.email}
    >
      <ColocaTabs
        key={organization.id}
        defaultValue="overview"
        contentClassName="pt-4"
        tabs={[
          {
            value: "overview",
            label: t("tab_overview"),
            content: (
              <OrganizationOverviewPanel
                organization={organization}
                state={overview}
                onRetry={onReloadOverview}
              />
            ),
          },
          {
            value: "users",
            label: t("tab_users"),
            content: <OrganizationUsersPanel state={overview} onRetry={onReloadOverview} />,
          },
          { value: "features", label: t("tab_features"), content: features },
        ]}
      />

      <DisablePayoutBatchesDialog
        organization={confirmingOff}
        isPending={isPending}
        onConfirm={(target) => {
          void onFeatureChange?.(target, "payout_batches_enabled", false).finally(() =>
            setConfirmingOff(null),
          );
        }}
        onOpenChange={(next) => !next && setConfirmingOff(null)}
      />
    </ColocaDrawer>
  );
}
